/**
 * Design tokens for NeuroGraph UI Micro Apps
 *
 * These tokens define the visual language shared across all micro apps.
 */

export const colors = {
  // Brand palette
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },

  // Semantic colors
  success: '#22c55e',
  successLight: '#86efac',
  successDark: '#16a34a',

  warning: '#f59e0b',
  warningLight: '#fcd34d',
  warningDark: '#d97706',

  danger: '#ef4444',
  dangerLight: '#fca5a5',
  dangerDark: '#dc2626',

  // Neutrals
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },

  // Review verdicts
  verdict: {
    strong: '#22c55e',
    promising: '#84cc16',
    needsWork: '#f59e0b',
    misaligned: '#ef4444',
  },

  // Score gradients
  scoreGradient: {
    low: { start: '#ef4444', end: '#f97316' },      // 1-4
    medium: { start: '#f59e0b', end: '#84cc16' },   // 5-7
    high: { start: '#22c55e', end: '#10b981' },     // 8-10
  },
} as const;

export const typography = {
  fonts: {
    sans: 'Inter, system-ui, sans-serif',
    mono: 'JetBrains Mono, Consolas, monospace',
  },

  sizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
  },

  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

export const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
} as const;

export const borderRadius = {
  none: '0',
  sm: '0.125rem',  // 2px
  md: '0.375rem',  // 6px
  lg: '0.5rem',    // 8px
  xl: '0.75rem',   // 12px
  full: '9999px',
} as const;

export const shadows = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
} as const;

// Skill-specific color mappings
export const hookTypeColors: Record<string, string> = {
  result_first: '#0ea5e9',
  call_out: '#8b5cf6',
  myth_buster: '#f59e0b',
  controversial: '#ef4444',
  before_after: '#22c55e',
  reverse_psych: '#ec4899',
  question: '#6366f1',
  statistic: '#14b8a6',
  story_open: '#f97316',
};

export const persuasionPrincipleColors: Record<string, string> = {
  reciprocity: '#0ea5e9',
  commitment: '#8b5cf6',
  social_proof: '#22c55e',
  authority: '#f59e0b',
  liking: '#ec4899',
  scarcity: '#ef4444',
  unity: '#6366f1',
};

export const recommendationPriorityColors: Record<string, string> = {
  critical: '#ef4444',
  high: '#f59e0b',
  medium: '#0ea5e9',
  low: '#a3a3a3',
};

/**
 * Get color for a score value (1-10 scale)
 */
export function getScoreColor(score: number): string {
  if (score >= 8) return colors.verdict.strong;
  if (score >= 5) return colors.verdict.promising;
  if (score >= 3) return colors.verdict.needsWork;
  return colors.verdict.misaligned;
}

/**
 * Get verdict color
 */
export function getVerdictColor(verdict: string): string {
  const key = verdict.toLowerCase().replace(/_/g, '') as keyof typeof colors.verdict;
  return colors.verdict[key] || colors.neutral[500];
}
